<template>
  <a-cascader
    v-model:value="innerValue"
    :options="options"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :show-search="showSearchConfig"
    :change-on-select="changeOnSelect"
    :expand-trigger="expandTrigger"
    :status="status"
    :display-render="displayRender"
    class="ant-cascader"
    @change="onChange"
  />
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";
import type { CascaderOption } from "./types";

type ValueType = Array<string | number> | null;

interface Props {
  modelValue?: ValueType;
  options?: CascaderOption[];
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  showSearch?: boolean;
  changeOnSelect?: boolean;
  expandTrigger?: "click" | "hover";
  status?: "" | "error" | "warning";
  separator?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  options: () => [],
  placeholder: "Chọn...",
  disabled: false,
  size: "middle",
  allowClear: true,
  showSearch: true,
  changeOnSelect: false,
  expandTrigger: "click",
  status: "",
  separator: " / ",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: ValueType): void;
  (e: "change", value: ValueType, selectedOptions?: CascaderOption[]): void;
}>();

const removeAccent = (str: string) =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const showSearchConfig = computed(() => {
  if (!props.showSearch) return false;
  return {
    filter: (inputValue: string, path: CascaderOption[]) =>
      path.some((opt) => removeAccent(String(opt.label)).includes(removeAccent(inputValue))),
  };
});

const displayRender = ({ labels }: { labels: string[] }) => labels.join(props.separator);

const innerValue = computed({
  get: () => props.modelValue,
  set: (val: ValueType) => {
    emit("update:modelValue", val);
  },
});

const onChange = (val: ValueType, selectedOptions?: CascaderOption[]) => {
  emit("update:modelValue", val);
  emit("change", val, selectedOptions);
};
</script>

<style scoped>
.ant-cascader {
  width: 100%;
}
</style>

<!--
  Hướng dẫn sử dụng — Cascader
  Global: <h-cascader v-model="path" :options="cascaderOptions" />
  Import: import { Cascader } from '@/components/ant/select'

  options: { label, value, children? }[]
  Props: modelValue (array), options, changeOnSelect, expandTrigger, showSearch...
  Events: update:modelValue, change
  Docs: src/components/ant/select/README.md
-->
